// HOW FAR APART THE EXPLODED VIEW PUTS A MODEL'S OWN MESHES — the Assembled / Exploded switch.
//
// OUT FROM THE CENTRE. Every mesh's centre is pushed away from the model's, along each axis by
// one factor for every mesh, so a left rail and a right rail part sideways, a backrest rises and
// a front frame comes forward — and no mesh passes another it was beside.
//
// THE FACTOR IS WHAT MAKES IT ABOUT A QUARTER BIGGER, per axis: the smallest that makes the
// meshes, spread, span EXPLODED_SPAN times what they span assembled. Per axis because a chair is
// not a cube — one factor for all three would grow its height a quarter and its width by whatever
// that happened to come to. An axis every mesh is centred on has nothing to push apart, and stays.

export const EXPLODED_SPAN = 1.25;
// The slide from one to the other, in milliseconds.
export const EXPLODE_MS = 450;
// How far a factor is looked for: a model whose meshes are nearly all centred on one axis gets as
// much spread as this allows there, rather than being flung across the room.
const MAX_FACTOR = 8;
const STEP = 0.01;

type Point = { x: number; y: number; z: number };
const AXES = ['x', 'y', 'z'] as const;

// EACH MESH'S SHIFT from where it stands assembled, in the frame its box is in, in their order.
//
// `along` IS FOR HARDWARE BUILT TOGETHER — a screw, its washer, the barrel nut it goes into, all
// on one axis (Z, see openai_blender.interactions_block). Out from the centre is wrong there: the
// washer's centre is above the screw's, so it was pushed up through the screw's head. Along the
// axis, in the order of their TOPS, is the order they come apart in: the screw lifts out first,
// the washer stays round its shank, the barrel nut drops away below — each one only ever moving
// off the end of whatever it is on.
export function explodeShifts(
  boxes: { min: Point; max: Point }[], along?: 'z',
): Point[] {
  const shifts = boxes.map(() => ({ x: 0, y: 0, z: 0 }));
  if (boxes.length < 2) return shifts;
  if (along) {
    const lo = Math.min(...boxes.map((b) => b.min[along]));
    const hi = Math.max(...boxes.map((b) => b.max[along]));
    // A GAP OF HALF THE WHOLE LENGTH, shared out: enough to clear a thread from its nut.
    const step = (hi - lo) * 0.5 / (boxes.length - 1);
    const order = boxes.map((b, i) => ({ top: b.max[along], i })).sort((a, b) => b.top - a.top);
    order.forEach(({ i }, rank) => { shifts[i][along] = ((boxes.length - 1) / 2 - rank) * step; });
    return shifts;
  }
  for (const a of AXES) {
    const lo = boxes.map((b) => b.min[a]);
    const hi = boxes.map((b) => b.max[a]);
    const centre = (Math.min(...lo) + Math.max(...hi)) / 2;
    const offs = boxes.map((b) => (b.min[a] + b.max[a]) / 2 - centre);
    const span = (k: number) => Math.max(...hi.map((h, i) => h + (k - 1) * offs[i]))
      - Math.min(...lo.map((l, i) => l + (k - 1) * offs[i]));
    const want = EXPLODED_SPAN * span(1);
    let k = 1;
    while (k < MAX_FACTOR && span(k) < want) k += STEP;
    offs.forEach((o, i) => { shifts[i][a] = (k - 1) * o; });
  }
  return shifts;
}

// Leaves and arrives at rest.
export const smoothstep = (k: number) => k * k * (3 - 2 * k);
